import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Heading, Modal, ProgressBar, useToast } from '@zillow/constellation-10';
import {
  ActionMetadataEntryPoint,
  type GetTourConfigAndAvailabilityWebApiResponseDto,
  type LocalTime,
  type TourConfirmationType,
  TourDuration,
} from '@zg-rentals/ts-z-calendar-client';
import { DatarouterError } from '@zg-rentals/web-base';
import TourCalendarConfig from './components/TourCalendarConfig';
import TourCalendarTimeZone from './components/TourCalendarTimeZone';
import TourAvailabilityCalendarFooter from './components/TourAvailabilityCalendarFooter';
import CalendarSettings from '../TourCalendarWithSettings/components/CalendarSettings';
import UnmaskedToast from '../UnmaskedToast';
import { TourAvailabilityWizardSkeleton } from './components/TourAvailabilityWizardSkeleton';
import TourCalendar from '../TourCalendar';
import { type TimeZoneIds, TOUR_CALENDAR_MODES, TourWizardSteps } from '../../constants/TourCalendar';
import { MOBILE_HANDOFF_TYPES, mobileHandoffHandler } from '../../utils/mobileAppCallbacks';
import useTourCalendarApi from '../../utils/tourCalendarHooks';
import type { ClickStreamMetadata } from '../../constants/TouringClickStreamEvents';
import {
  CALENDAR_CLICKSTREAM_ACTION,
  emitCalendarEventActionEvent,
  emitTourAvailabilityWizardImpresssionEvent,
  emitTourAvailabilityWizardTimeZoneNextClickEvent,
  emitTourTypeSelectionEvent,
  TourAvailabilityWizardImpresssionSourceMap,
} from '../../constants/TouringClickStreamEvents';

import TourCalendarContext from '../TourCalendarWithSettings/context/TourCalendarContext';

const SETTINGS_SAVE_SUCCESS_MESSAGE = 'Your tour setting changes were saved.';
const SETTINGS_SAVE_ERROR_MESSAGE = "Your tour setting changes couldn't be saved. Try again.";
const TOUR_DURATION_SAVE_SUCCESS_MESSAGE = 'Tour length updated successfully.';
const TOUR_DURATION_SAVE_ERROR_MESSAGE = "Tour length couldn't be updated. Try again.";

type Props = {
  onClose: () => void;
  listingAlias: string;
  modalHeaderText: string;
  isInMobileWebView?: boolean;
  modalHeaderLevel?: 1 | 2 | 3 | 4 | 5 | 6;
  clickStreamMetadata?: ClickStreamMetadata;
  hideCloseButton?: boolean;
  canAccessPostingPathTourDuration?: boolean;
  propertyPhotoUrl?: string;
};

const TourAvailabilityWizard = ({
  onClose,
  listingAlias,
  modalHeaderText,
  isInMobileWebView = false,
  modalHeaderLevel,
  clickStreamMetadata,
  hideCloseButton = false,
  canAccessPostingPathTourDuration = false,
  propertyPhotoUrl,
}: Props) => {
  const { enqueueToast } = useToast();
  const mode = TOUR_CALENDAR_MODES.TOUR_AVAILABILITY;
  const [wizardStep, setWizardStep] = useState<TourWizardSteps>(TourWizardSteps.CONFIG);
  const [wizardTourConfirmationType, setWizardTourConfirmationType] = useState<TourConfirmationType>();
  const [wizardTimeZone, setWizardTimeZone] = useState<TimeZoneIds>();
  const [wizardTourDuration, setWizardTourDuration] = useState<TourDuration | undefined>(TourDuration.MINUTES_30);
  const [hasEnabledTouring, setHasEnabledTouring] = useState(false);
  const [displaySettings, setDisplaySettings] = useState(false);

  const onGetTourConfigAndAvailabilitySuccess = useCallback(
    (data: GetTourConfigAndAvailabilityWebApiResponseDto) => {
      const trackTourAvailabilityWizardImpression = (source: TourAvailabilityWizardImpresssionSourceMap) => {
        if (clickStreamMetadata) {
          emitTourAvailabilityWizardImpresssionEvent(clickStreamMetadata, source);
        }
      };

      if ('tourConfig' in data && data.tourConfig) {
        const confirmationType = data.tourConfig.tourSchedulingTypes.schedulingTypes[0].confirmationType;
        const zoneId = data.tourConfig.zoneId;

        if (confirmationType && zoneId) {
          setWizardStep?.(TourWizardSteps.CALENDAR);
          setHasEnabledTouring(true);
          trackTourAvailabilityWizardImpression(TourAvailabilityWizardImpresssionSourceMap.TOUR_EVENT);
          return;
        }

        if (confirmationType) {
          setWizardStep?.(TourWizardSteps.TIME_ZONE);
          trackTourAvailabilityWizardImpression(TourAvailabilityWizardImpresssionSourceMap.PICK_TOUR_TIME_NEXT);
          return;
        }

        trackTourAvailabilityWizardImpression(TourAvailabilityWizardImpresssionSourceMap.PICK_TOUR_TYPE_NEXT);
        return;
      }

      trackTourAvailabilityWizardImpression(TourAvailabilityWizardImpresssionSourceMap.PICK_TOUR_TYPE_NEXT);
    },
    [setWizardStep],
  );

  const {
    availabilityEvents,
    tourEvents,
    timeZone: timeZoneFromApi,
    tourConfirmationType: tourConfirmationTypeFromApi,
    tourDuration: tourDurationFromApi,
    addAvailabilityEvent,
    updateAvailabilityEvent,
    removeAvailabilityEvent,
    performTourAction,
    enableTouringForListing,
    disableTouringForListing,
    isGetTourConfigAndAvailabilityLoading,
    isEnableTouringForListingLoading,
    isDisableTouringForListingLoading,
    isTouringSchedulingEnabled,
    storeListingCalendarTimeConfig,
    storeListingTourDuration,
    calendarStartTime,
    calendarEndTime,
    refetchTouringData,
  } = useTourCalendarApi({
    listingAlias,
    mode,
    onGetTourConfigAndAvailabilitySuccess,
  });

  const renderHeader = () => {
    return (
      <Heading level={modalHeaderLevel ?? 5} data-testid="TourAvailabilityWizard-ModalHeader">
        {modalHeaderText}
      </Heading>
    );
  };

  const mobileWebViewProps = useMemo(
    () => (isInMobileWebView ? { closeButton: <span hidden />, header: null } : {}),
    [isInMobileWebView],
  );

  const onTourTypeSelection = (tourConfirmationType: TourConfirmationType) => {
    setWizardTourConfirmationType(tourConfirmationType);
  };

  const onTimeZoneSelection = (timeZone: TimeZoneIds) => {
    setWizardTimeZone(timeZone);
  };

  // Initialize with existing API tour duration if available
  useEffect(() => {
    if (tourDurationFromApi) {
      setWizardTourDuration(tourDurationFromApi);
    }
  }, [tourDurationFromApi]);

  const onTourDurationSelection = (selectedTourDuration: TourDuration) => {
    setWizardTourDuration(selectedTourDuration);
  };

  const handleSettingsSelectTourConfirmationType = useCallback(
    (type: TourConfirmationType) => {
      enableTouringForListing(type, timeZoneFromApi, tourDurationFromApi, ActionMetadataEntryPoint.WIZARD)
        .then(() => {
          enqueueToast(<UnmaskedToast tone="success">{SETTINGS_SAVE_SUCCESS_MESSAGE}</UnmaskedToast>);
          return;
        })
        .catch(() => {
          enqueueToast(<UnmaskedToast tone="critical">{SETTINGS_SAVE_ERROR_MESSAGE}</UnmaskedToast>);
        });
    },
    [timeZoneFromApi, tourDurationFromApi, enableTouringForListing, enqueueToast],
  );

  const handleTourSchedulingToggle = useCallback(() => {
    disableTouringForListing()
      .then(() => onClose())
      .catch((error) => {
        const errorMessage = DatarouterError.getDisplayMessage(error);
        enqueueToast(<UnmaskedToast tone="critical">{errorMessage}</UnmaskedToast>);
      });
  }, [disableTouringForListing, enqueueToast, onClose]);

  const handleChangeCalendarConfigTime = useCallback(
    (startTime: LocalTime, endTime: LocalTime) => {
      storeListingCalendarTimeConfig(startTime, endTime)
        .then(() => {
          enqueueToast(<UnmaskedToast tone="success">{SETTINGS_SAVE_SUCCESS_MESSAGE}</UnmaskedToast>);
          return;
        })
        .catch(() => {
          enqueueToast(<UnmaskedToast tone="critical">{SETTINGS_SAVE_ERROR_MESSAGE}</UnmaskedToast>);
        });
    },
    [enqueueToast, storeListingCalendarTimeConfig],
  );

  const handleChangeTourDuration = useCallback(
    (duration: TourDuration) => {
      storeListingTourDuration(duration)
        .then(() => {
          enqueueToast(<UnmaskedToast tone="success">{TOUR_DURATION_SAVE_SUCCESS_MESSAGE}</UnmaskedToast>);
          return;
        })
        .catch(() => {
          enqueueToast(<UnmaskedToast tone="critical">{TOUR_DURATION_SAVE_ERROR_MESSAGE}</UnmaskedToast>);
        });
    },
    [enqueueToast, storeListingTourDuration],
  );

  const renderBody = () => {
    if (isGetTourConfigAndAvailabilityLoading) {
      return (
        <Box
          css={{
            display: 'grid',
            gridTemplateColumns: 'repeat(24, 1fr)',
            marginTop: { base: isInMobileWebView ? undefined : 'default', md: '48px' },
          }}
        >
          <TourAvailabilityWizardSkeleton />
        </Box>
      );
    }

    let component;
    let progressBarValue = 1;

    if (wizardStep === TourWizardSteps.CONFIG) {
      component = (
        <TourCalendarConfig
          onTourTypeSelection={onTourTypeSelection}
          tourConfirmationType={wizardTourConfirmationType}
        />
      );
    } else if (wizardStep === TourWizardSteps.TIME_ZONE) {
      component = (
        <TourCalendarTimeZone
          onTimeZoneSelection={onTimeZoneSelection}
          onTourDurationSelection={onTourDurationSelection}
          timeZone={wizardTimeZone}
          tourDuration={wizardTourDuration}
          disableTimeZoneSelection={Boolean(timeZoneFromApi)}
          canAccessPostingPathTourDuration={canAccessPostingPathTourDuration}
        />
      );
      progressBarValue = 2;
    } else if (timeZoneFromApi && tourConfirmationTypeFromApi) {
      component = (
        <TourCalendarContext.Provider
          value={{
            clickStreamMetadata,
            listingAlias,
            listingAddress: modalHeaderText,
            propertyPhotoUrl,
            refetchTouringData,
          }}
        >
          <TourCalendar
            availabilityEvents={availabilityEvents}
            tourEvents={tourEvents}
            addAvailabilityEvent={addAvailabilityEvent}
            updateAvailabilityEvent={updateAvailabilityEvent}
            removeAvailabilityEvent={removeAvailabilityEvent}
            performTourAction={performTourAction}
            mode={mode}
            onClickSettings={() => setDisplaySettings(true)}
            timeZone={timeZoneFromApi}
            clickStreamMetadata={clickStreamMetadata}
            isInMobileWebView={isInMobileWebView}
            listingAlias={listingAlias}
            calendarStartTime={calendarStartTime}
            calendarEndTime={calendarEndTime}
            tourDuration={wizardTourDuration}
          />
        </TourCalendarContext.Provider>
      );
      progressBarValue = 3;
    }

    return (
      <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(24, 1fr)' }}>
        {!hasEnabledTouring ? (
          <Box
            css={{
              gridColumnStart: { base: '2', md: '4' },
              gridColumnEnd: { base: 'span 22', md: 'span 18' },
              marginTop: { base: 'default', md: '48px' },
              marginBottom: { base: 'loose', md: 'looser' },
            }}
          >
            <ProgressBar maxValue={3} value={progressBarValue} data-testid="TourAvailabilityWizard-ProgressBar" />
          </Box>
        ) : (
          <Box
            css={{
              gridColumnStart: '1',
              gridColumnEnd: '24',
              marginTop: { base: isInMobileWebView ? undefined : 'default', md: '48px' },
            }}
          />
        )}
        {component}
      </Box>
    );
  };

  const onClickBack = () => {
    if (wizardStep === TourWizardSteps.TIME_ZONE) {
      setWizardStep(TourWizardSteps.CONFIG);
    } else if (wizardStep === TourWizardSteps.CALENDAR) {
      setWizardStep(TourWizardSteps.TIME_ZONE);
    }
  };

  const onClickNext = () => {
    if (wizardStep === TourWizardSteps.CONFIG) {
      if (clickStreamMetadata && wizardTourConfirmationType) {
        emitTourTypeSelectionEvent(clickStreamMetadata, wizardTourConfirmationType);
      }
      return setWizardStep(TourWizardSteps.TIME_ZONE);
    } else if (wizardStep === TourWizardSteps.TIME_ZONE && wizardTourConfirmationType && wizardTimeZone) {
      return enableTouringForListing(
        wizardTourConfirmationType,
        wizardTimeZone,
        wizardTourDuration,
        ActionMetadataEntryPoint.WIZARD,
      ).then(() => {
        if (clickStreamMetadata) {
          emitTourAvailabilityWizardTimeZoneNextClickEvent(clickStreamMetadata);
        }
        return setWizardStep(TourWizardSteps.CALENDAR);
      });
    } else if (wizardStep === TourWizardSteps.CALENDAR) {
      if (clickStreamMetadata) {
        try {
          emitCalendarEventActionEvent(clickStreamMetadata, CALENDAR_CLICKSTREAM_ACTION.ADD_TO_LISTING_CTA);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error tracking wizard calendar cta', error);
        }
      }

      if (isInMobileWebView) {
        return mobileHandoffHandler(MOBILE_HANDOFF_TYPES.TOUR_ADDED_SUCCEEDED);
      }

      return onClose();
    }
  };

  const isNextStepDisabled = () => {
    if (wizardStep === TourWizardSteps.CONFIG) {
      return !wizardTourConfirmationType;
    } else if (wizardStep === TourWizardSteps.TIME_ZONE) {
      return (
        !isEnableTouringForListingLoading && (!wizardTourConfirmationType || !wizardTimeZone || !wizardTourDuration)
      );
    }

    return false;
  };

  const closeButtonProps = hideCloseButton ? { closeButton: <span hidden /> } : {};

  return (
    <>
      <Modal
        open={true}
        onOpenChange={(open) => open === false && onClose()}
        header={renderHeader()}
        body={renderBody()}
        footer={
          isGetTourConfigAndAvailabilityLoading ? null : (
            <TourAvailabilityCalendarFooter
              isNextDisabled={isNextStepDisabled()}
              onClickBack={onClickBack}
              onClickNext={onClickNext}
              step={wizardStep}
              hasEnabledTouring={hasEnabledTouring}
            />
          )
        }
        size="fullScreen"
        {...closeButtonProps}
        {...mobileWebViewProps}
      />
      {timeZoneFromApi && tourConfirmationTypeFromApi && (
        <Modal.Root open={displaySettings} size="fullScreen" scrollDocument>
          <Modal.Portal>
            <Modal.Backdrop>
              <Modal.Content>
                {/* Modal.Header must be rendered (not omitted) to avoid reserved header space;
                   hiding it with display:none suppresses both the header area and close button. */}
                <Modal.Header css={{ display: 'none' }}>
                  <span />
                </Modal.Header>
                <CalendarSettings
                  timeZone={timeZoneFromApi}
                  tourConfirmationType={tourConfirmationTypeFromApi}
                  tourDuration={tourDurationFromApi}
                  canEditTourDuration={canAccessPostingPathTourDuration}
                  onSelectTourConfirmationType={handleSettingsSelectTourConfirmationType}
                  onClickBack={() => setDisplaySettings(false)}
                  isTouringSchedulingEnabled={isTouringSchedulingEnabled}
                  onTourSchedulingToggle={handleTourSchedulingToggle}
                  isDisableTouringForListingLoading={isDisableTouringForListingLoading}
                  calendarStartTime={calendarStartTime}
                  calendarEndTime={calendarEndTime}
                  onChangeCalendarConfigTime={handleChangeCalendarConfigTime}
                  onChangeTourDuration={handleChangeTourDuration}
                />
              </Modal.Content>
            </Modal.Backdrop>
          </Modal.Portal>
        </Modal.Root>
      )}
    </>
  );
};

export default TourAvailabilityWizard;
